<template>
  <div>
    <div>
      <div class="btn-group me-2">
        <button
          class="btn btn-sm btn-outline-primary px-4"
          type="button"
          :disabled="isRendering"
          @click="printDocument"
        >
          <i class="material-icons align-middle" style="font-size: 1rem">print</i>
          <span class="ms-1">{{
            $t("templatePages.printOrExport") || "Print or export"
          }}</span>
        </button>
        <button
          class="btn btn-sm btn-outline-primary px-4"
          type="button"
          :disabled="isRendering"
          @click="exportToExcel"
        >
          <i class="fas fa-file-excel me-2"></i>Excel
        </button>
      </div>
    </div>

    <div class="shadow-container">
      <ContentSkeleton v-if="isRendering" variant="block" height="12rem" />
      <div
        ref="shadowHost"
        class="shadow-host"
        :class="{ 'is-rendering': isRendering }"
        id="html-export-table"
      ></div>
    </div>
  </div>
</template>

<script>
/**
 * ViewHtmlContent Component
 *
 * Este componente renderiza contenido HTML con soporte para:
 * - Shortcodes de campos: [nombre], [cantidad], etc.
 * - Campos anidados de grupos: [grupo1>subgrupo>campo]
 * - Referencias anidadas: [referencia>campo]
 * - Vistas anidadas: [view:id]
 *
 * Soporte para múltiples niveles de anidación:
 * - Nivel 1: [grupo1>campo]
 * - Nivel 2: [grupo1>subgrupo>campo]
 * - Nivel 3: [grupo1>subgrupo>subsubgrupo>campo]
 * - Nivel N: [grupo1>...>grupoN>campo]
 *
 * Ejemplos de uso:
 * - [cliente>nombre] - Campo nombre del grupo cliente
 * - [cliente>direccion>calle] - Campo calle del subgrupo direccion dentro de cliente
 * - [cliente>contacto>telefono>numero] - Campo numero del subgrupo telefono dentro de contacto
 *
 * Labels traducibles (i18n): usar {t:clave}. Se reemplaza con $t('clave').
 * Ejemplo: {t:Brokers} → $t('Brokers'). Shortcodes de datos siguen siendo [campo].
 *
 * Grupo + isArray: por defecto se sustituye por una tabla (thead con th por subcampo,
 * tbody con una fila por elemento). Alternativa: field.htmlGroupArrayFormat === 'stacked'
 * o view.design.htmlGroupArrayFormats[campoKey] === 'stacked' (bloques apilados).
 *
 * Anidamiento: solo dos niveles de grupo con maquetación rica (raíz + un subgrupo).
 * A partir del tercer nivel se muestra un bloque compacto (lista etiqueta · valor).
 *
 * Columnas de grupo (opcional): último segmento del path con `:clave1,clave2`
 * Ej.: [lineas:sku,cantidad] · [pedido>lineas:sku,cantidad]
 * También: view.design.htmlGroupColumnKeys[ruta] y htmlGroupHideTableHeader[ruta]
 * (ruta = clave o padre>hijo>...) para anidados sin shortcode.
 * htmlGroupPanelLabels: títulos/etiquetas por ruta; en pantalla se pasan por translateStoredViewHtmlLabel
 * (clave i18n como en el modelo, o fragmentos {t:clave} como en el HTML).
 *
 * Formato de fecha (opcional): `||df:DD/MM/YYYY` en el shortcode o view.design.htmlDateFormats[ruta].
 * Ej.: [fechaContrato||df:DD/MM/YYYY] · [grupo>fecha||df:MMMM YYYY]
 */
import { wencesApi, getCurrentClientDbName } from "@/api/wencesApi";
import { dbNameMatchesTenant } from "@/utils/dbNameMatchesTenant";
import html2pdf from "html2pdf.js";
import exportMixin from "@/mixins/exportMixin";
import { trackActivity } from "@/utils/activityTracker";
import { formatNumberSync, getNumberFormat, setNumberFormatCache } from "@/utils/numberFormatter";
import {
  formatDateWithHtmlPattern,
  parseHtmlShortcodeDateFormatFromExtras,
  resolveHtmlShortcodeDateFormat,
  isHtmlShortcodeDateField,
} from "@/utils/htmlShortcodeDateFormat";
import ContentSkeleton from "@/components/common/ContentSkeleton.vue";

export default {
  mixins: [exportMixin],
  name: "ViewHtmlContent",
  components: { ContentSkeleton },
  props: {
    view: {
      type: Object,
      required: true,
    },
    value: {
      type: Object,
      required: true,
    },
    fields: {
      type: Array,
      default: () => [],
    },
    lists: {
      type: Array,
      default: () => [],
    },
    models: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      processedHtmlContent: "",
      referenceOptions: {},
      shadowRoot: null,
      processedViews: new Set(), // Para evitar bucles infinitos en vistas anidadas
      /** Mientras se resuelven shortcodes/vistas anidadas y se pinta el Shadow DOM */
      isRendering: true,
      /** Cada llamada a processHtmlAsync toma un número; solo la última apaga el loading */
      renderSeq: 0,
      /** Overrides de chrome de módulo (p. ej. alisof.ct.*) para {t:clave} */
      moduleChromeTranslations: null,
    };
  },
  async mounted() {
    // Cargar el formato de números si no está en cache
    try {
      const format = await getNumberFormat();
      setNumberFormatCache(format);
      console.log('📊 ViewHtmlContent: Number format loaded:', format);
      console.log('📊 ViewHtmlContent: localStorage format:', localStorage.getItem("numberFormat"));
    } catch (error) {
      console.error("❌ Error loading number format:", error);
    }
    
    this.$nextTick(() => {
      this.processHtmlAsync();
    });
  },
  beforeDestroy() {
    // Limpiar el Shadow DOM
    this.clearShadowDOM();
  },
  watch: {
    value: {
      handler() {
        this.moduleChromeTranslations = null;
        this.processHtmlAsync();
      },
      deep: true,
    },
    fields: {
      handler() {
        this.processHtmlAsync();
      },
      deep: true,
    },
    models: {
      handler() {
        this.processHtmlAsync();
      },
      deep: true,
    },
    view: {
      handler() {
        this.moduleChromeTranslations = null;
        this.processHtmlAsync();
      },
      deep: true,
    },
    "view.design.htmlCode"() {
      this.moduleChromeTranslations = null;
      this.processHtmlAsync();
    },
  },
  computed: {
    processedHtml() {
      return this.processedHtmlContent;
    },
  },
  methods: {
    /**
     * Modelo definido para un field.ref. Tolera diferencias de caja/plural
     * (p. ej. ref "users" cuando el modelo está definido como "User"), que si no
     * dejan el shortcode de la referencia en blanco.
     * @param {string} ref
     * @returns {object|null}
     */
    findModelByRef(ref) {
      const models = Array.isArray(this.models) ? this.models : [];
      if (!ref) return null;
      const exact = models.find((m) => m && m.modelName === ref);
      if (exact) return exact;
      const target = String(ref).trim().toLowerCase();
      if (!target) return null;
      const normalize = (name) =>
        String(name || "")
          .trim()
          .toLowerCase();
      const insensitive = models.find(
        (m) => m && normalize(m.modelName) === target
      );
      if (insensitive) return insensitive;
      const singular = target.replace(/s$/, "");
      return (
        models.find(
          (m) => m && normalize(m.modelName).replace(/s$/, "") === singular
        ) || null
      );
    },
    /**
     * Nibiru: título propio del documento (solo tenant nibiru).
     * - Invoice PDF (shortcodes [nibiru:inv_*]) → nombre del shipping
     * - Proforma PDF (shortcodes [nibiru:pi_*]) → "PROFORMA <proform_number>"
     * @returns {string|null} nombre sin extensión, o null si no aplica
     */
    getNibiruPdfFileName() {
      if (!dbNameMatchesTenant(getCurrentClientDbName(), "nibiru")) return null;

      const htmlCode = String(this.view?.design?.htmlCode || "");
      const label = `${this.view?.label || ""} ${
        this.view?.name || ""
      }`.toLowerCase();
      const hasProformaCode = /\[\s*nibiru:pi_/i.test(htmlCode);
      const hasInvoiceCode = /\[\s*nibiru:inv_/i.test(htmlCode);

      const isProforma =
        hasProformaCode || (!hasInvoiceCode && label.includes("proform"));
      if (isProforma) {
        const proformNumber =
          this.value?.proform_number ||
          this.value?.proform?.proform_number ||
          this.value?.custom_proform?.proform_number ||
          null;
        return proformNumber ? `PROFORMA ${proformNumber}` : null;
      }

      const isInvoice =
        hasInvoiceCode || (!hasProformaCode && label.includes("invoice"));
      if (isInvoice) {
        // La vista de invoice es sobre shipping: value.name es el nombre del shipping
        const shippingName =
          this.value?.shipping?.name || this.value?.name || null;
        return shippingName ? String(shippingName) : null;
      }

      return null;
    },
    // Helper para generar el nombre del archivo PDF
    getPdfFileName() {
      const nibiruFileName = this.getNibiruPdfFileName();
      if (nibiruFileName) return nibiruFileName;

      // Solo para el template instructions-duntower, usar formato especial: "Shipping Instructions PI" + proform_number
      const isInstructionsDuntower = 
        (this.view?.label && this.view.label.toLowerCase().includes("instructions-duntower")) ||
        (this.view?.name && this.view.name.toLowerCase().includes("instructions-duntower")) ||
        (this.view?.label && this.view.label.toLowerCase().includes("duntower") && this.view.label.toLowerCase().includes("instruction")) ||
        (this.view?.name && this.view.name.toLowerCase().includes("duntower") && this.view.name.toLowerCase().includes("instruction")) ||
        (this.$route?.params?.type === 'instructions-duntower') ||
        (this.$route?.path && this.$route.path.includes('instructions-duntower'));
      
      if (isInstructionsDuntower && this.value) {
        // Buscar proform_number en diferentes ubicaciones posibles
        const proformNumber = 
          this.value.proform?.proform_number ||
          this.value.custom_proform?.proform_number ||
          (typeof this.value.custom_proform === 'object' && this.value.custom_proform?.proform_number) ||
          (typeof this.value.proform === 'object' && this.value.proform?.proform_number) ||
          this.value.proform_number;
        
        if (proformNumber) {
          return `Shipping Instructions PI ${proformNumber}`;
        }
      }
      
      // Para otros tipos de documentos, usar el formato original
      return `${
        this.view?.label || this.view?.name || "documento"
      }_${this.value?.idModel || this.value?._id || ""}`;
    },
    /** Registro de actividad: qué documento imprime o exporta (ver activityTracker). */
    trackDocumentAction(action) {
      trackActivity("action", {
        route: action,
        model: (this.view && this.view.modelName) || "",
        itemId: (this.value && this.value._id) || "",
        data: { view: this.view && (this.view.name || this.view._id) },
      });
    },
    /**
     * Misma idea que DocumentTemplate.printDocument / printInNewWindow: solo diálogo de impresión,
     * sin generar ni descargar PDF (el envío por email sigue usando getPdfForEmail + html2pdf).
     */
    printDocument() {
      this.trackDocumentAction("document_print");
      if (!this.shadowRoot || !this.shadowRoot.innerHTML.trim()) {
        this.$root.$emit(
          "show-error",
          this.$t("No content to export") || "No hay contenido para imprimir"
        );
        return;
      }

      const titleRaw = this.getPdfFileName();
      const safeTitle = String(titleRaw)
        .replace(/&/g, "&amp;")
        .replace(/</g, " ")
        .replace(/>/g, " ")
        .replace(/"/g, "'")
        .slice(0, 200);

      const inner = this.shadowRoot.innerHTML;
      const printWindow = window.open("", "_blank");
      if (!printWindow) {
        this.$root.$emit(
          "show-error",
          this.$t("templatePages.popupBlocked") ||
            "Permite ventanas emergentes para imprimir."
        );
        return;
      }

      printWindow.document.write(`<!DOCTYPE html>
<html>
  <head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${safeTitle}</title>
    <style>
      html, body {
        margin: 0;
        padding: 0;
        background: white !important;
        background-color: white !important;
        font-family: Arial, sans-serif;
      }
      @media print {
        @page {
          margin: 0.5cm;
          size: A4;
        }
        html, body {
          margin: 0 !important;
          padding: 0 !important;
          background: white !important;
          background-color: white !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
          height: auto !important;
          max-height: none !important;
        }
        .view-html-content {
          position: static !important;
          margin: 0 auto !important;
          padding: 8mm !important;
          width: 100% !important;
          max-width: 210mm !important;
          background: white !important;
          background-color: white !important;
          height: auto !important;
          min-height: auto !important;
          overflow: visible !important;
          box-shadow: none !important;
          page-break-inside: auto !important;
        }
        .view-html-content * {
          overflow: visible !important;
        }
        .nested-view {
          page-break-inside: avoid !important;
          break-inside: avoid !important;
        }
      }
    </style>
  </head>
  <body>
    ${inner}
  </body>
</html>`);
      printWindow.document.close();

      printWindow.onload = () => {
        setTimeout(() => {
          printWindow.focus();
          printWindow.print();
        }, 250);
      };
    },
    async exportToExcel() {
      this.trackDocumentAction("document_excel");
      const fileName = `${this.view?.label || this.view?.name || "documento"}_${
        this.value?.idModel || this.value?._id || ""
      }`;

      // Crear mapeo de campos para la extracción de datos
      const fieldMapping = {};
      this.fields.forEach((field) => {
        fieldMapping[field.key] = field.label || field.key;
      });

      try {
        // Intentar exportar desde HTML del Shadow DOM primero
        if (this.shadowRoot) {
          const element = this.shadowRoot.querySelector(".view-html-content");
          if (element) {
            try {
              // Primero intentar con el método directo del Shadow DOM
              const success = await this.exportToExcelFromShadowDOM(
                element,
                fileName,
                fieldMapping
              );
              if (success) return;
            } catch (error) {
              console.warn(
                "Error con exportación directa del Shadow DOM, intentando clonación:",
                error
              );
            }

            // Si falla, intentar con clonación
            const tempElement = element.cloneNode(true);
            tempElement.id = "html-export-table";

            // Asegurar que el elemento sea visible y accesible
            tempElement.style.position = "absolute";
            tempElement.style.left = "-9999px";
            tempElement.style.top = "-9999px";
            tempElement.style.visibility = "hidden";

            document.body.appendChild(tempElement);

            try {
              // Esperar un momento para que el DOM se actualice
              await new Promise((resolve) => setTimeout(resolve, 100));
              await this.exportHtmlToExcel("html-export-table", fileName, {
                fieldMapping,
              });
            } finally {
              // Limpiar el elemento temporal
              if (document.body.contains(tempElement)) {
                document.body.removeChild(tempElement);
              }
            }
            return;
          }
        }

        // Si no hay Shadow DOM, intentar con el host
        await this.exportHtmlToExcel("html-export-table", fileName, {
          fieldMapping,
        });
      } catch (error) {
        console.warn(
          "Error exportando HTML a Excel, intentando exportación de datos estructurados:",
          error
        );
        // Si no hay contenido HTML, exportar datos estructurados
        await this.exportModelToExcel(
          this.fields,
          this.value,
          fileName,
          this.lists,
          this.models
        );
      }
    },
    /**
     * Generate PDF (html2pdf) and return base64 + fileName for email.
     * Used by dynamic-model-display for the attachment.
     */
    async getPdfForEmail() {
      const element = this.shadowRoot
        ? this.shadowRoot.querySelector(".view-html-content")
        : this.$refs.shadowHost;
      if (!element) {
        throw new Error("No content to export");
      }
      const cleanFileName = this.getPdfFileName()
        .replace(/[^a-zA-Z0-9_-]/g, "_")
        .replace(/_+/g, "_")
        .replace(/^_|_$/g, "");
      const opt = {
        margin: 0,
        filename: `${cleanFileName}.pdf`,
        image: { type: "jpeg", quality: 0.92 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: "#ffffff",
          letterRendering: true,
          logging: false,
        },
        jsPDF: {
          unit: "cm",
          format: "a4",
          orientation: "portrait",
        },
      };
      const pdfBlob = await html2pdf().set(opt).from(element).outputPdf("blob");
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = reader.result;
          resolve(dataUrl.replace(/^data:application\/pdf;base64,/, ""));
        };
        reader.onerror = reject;
        reader.readAsDataURL(pdfBlob);
      });
      return { base64, fileName: `${cleanFileName}.pdf` };
    },
    /**
     * Reemplaza en el HTML los labels de traducción {t:clave}.
     * Prioriza chrome de módulo (Alisof PDF), luego vue-i18n $t.
     */
    processTranslationLabels(html) {
      if (!html || typeof html !== 'string') return html;
      return html.replace(/\{t:([^}]+)\}/g, (match, key) => {
        const trimmed = key.trim();
        if (!trimmed) return match;
        const fromModule =
          this.moduleChromeTranslations && this.moduleChromeTranslations[trimmed];
        if (fromModule != null && String(fromModule).trim() !== '') {
          return String(fromModule);
        }
        const t = this.$t(trimmed);
        return typeof t === 'string' ? t : trimmed;
      });
    },
    /**
     * Carga etiquetas {t:alisof.*} desde API Alisof (no viven en locales del front).
     */
    async ensureModuleChromeTranslations(html) {
      if (!html || typeof html !== 'string') return;
      if (!/\{t:alisof\./.test(html)) return;
      if (this.moduleChromeTranslations) return;

      const viewId = this.view?._id != null ? String(this.view._id) : '';
      const rawId = this.value?._id;
      const itemId =
        rawId != null && typeof rawId === 'object'
          ? rawId.toString?.() || String(rawId)
          : rawId != null
            ? String(rawId)
            : '';
      const modelName = this.view?.modelName || 'Contrato';

      try {
        const { data } = await wencesApi.get('/api/alisof/translations/chrome', {
          params: {
            viewId: viewId || undefined,
            itemId: itemId || undefined,
            modelName,
          },
        });
        if (data && data.success && data.data && data.data.translations) {
          this.moduleChromeTranslations = data.data.translations;
        } else {
          this.moduleChromeTranslations = {};
        }
      } catch (_err) {
        this.moduleChromeTranslations = {};
      }
    },
    /**
     * Texto guardado en diseño de vista (p. ej. htmlGroupPanelLabels): clave i18n como en campos del modelo,
     * o sintaxis {t:clave} igual que en el HTML de la vista.
     */
    translateStoredViewHtmlLabel(text) {
      if (text == null) return "";
      const str = String(text).trim();
      if (!str) return "";
      if (str.includes("{t:")) {
        const out = this.processTranslationLabels(str);
        return typeof out === "string" ? out : str;
      }
      const t = this.$t(str);
      return typeof t === "string" ? t : str;
    },
    async processHtmlAsync() {
      // Los watchers (value/fields/models/view, todos deep) pueden encadenar varias
      // pasadas; solo la última apaga el loading.
      const seq = ++this.renderSeq;
      this.isRendering = true;
      try {
        await this.buildAndPaintHtml();
      } catch (error) {
        console.error("Error rendering view HTML:", error);
      } finally {
        if (seq === this.renderSeq) this.isRendering = false;
      }
    },
    async buildAndPaintHtml() {
      if (!this.view.design || !this.view.design.htmlCode) {
        this.processedHtmlContent = "";
        this.clearShadowDOM();
        return;
      }

      // Limpiar el set de vistas procesadas para permitir un nuevo procesamiento
      this.processedViews.clear();

      let html = this.view.design.htmlCode;

      // Chrome Alisof ({t:alisof.ct.*}) no está en vue-i18n: cargar mapa del módulo
      await this.ensureModuleChromeTranslations(html);

      // Traducir labels {t:clave} (antes de shortcodes)
      html = this.processTranslationLabels(html);

      // Primero, procesar las vistas anidadas (shortcodes de vistas)
      const viewShortcodeRegex = /\[view:([^\]]+)\]/g;
      const viewMatches = html.match(viewShortcodeRegex) || [];

      for (const viewShortcode of viewMatches) {
        const viewId = viewShortcode.match(/\[view:([^\]]+)\]/)[1];
        const nestedViewHtml = await this.renderNestedView(viewId);
        html = html.replace(viewShortcode, nestedViewHtml);
      }

      // Luego, procesar los shortcodes de campos normales
      const shortcodeRegex = /\[([^\]]+)\]/g;
      const matches = html.match(shortcodeRegex) || [];

      for (const shortcode of matches) {
        const fieldKey = shortcode.slice(1, -1); // Remover los corchetes

        const value = await this.getSyncValueForField(fieldKey);

        html = html.replace(shortcode, value);
      }

      this.processedHtmlContent = html;

      // Usar nextTick para asegurar que el DOM esté listo
      await this.$nextTick();
      await this.createShadowDOM(html);
    },
    async renderNestedView(viewId) {
      // Evitar bucles infinitos si una vista ya se ha procesado

      this.processedViews.add(viewId);

      try {
        // Obtener la vista anidada desde la API
        const { data: nestedView } = await wencesApi.get(
          `/api/gotrading/views/${viewId}`
        );

        if (!nestedView || nestedView.type !== "viewhtml") {
          console.warn(
            `View with ID ${viewId} not found or is not an HTML view.`
          );
          return `<div class="nested-view-error">
            <i class="fas fa-exclamation-triangle"></i> Vista no encontrada o no es una vista HTML
          </div>`;
        } else {
        }

        // Procesar el HTML de la vista anidada con los mismos datos
        let nestedHtml = nestedView.design.htmlCode;

        // Traducir labels {t:clave} en la vista anidada
        nestedHtml = this.processTranslationLabels(nestedHtml);

        // Procesar vistas anidadas dentro de la vista anidada (recursivo)
        const nestedViewShortcodeRegex = /\[view:([^\]]+)\]/g;
        const nestedViewMatches =
          nestedHtml.match(nestedViewShortcodeRegex) || [];

        for (const nestedViewShortcode of nestedViewMatches) {
          const nestedViewId =
            nestedViewShortcode.match(/\[view:([^\]]+)\]/)[1];
          const deeplyNestedHtml = await this.renderNestedView(nestedViewId);
          nestedHtml = nestedHtml.replace(
            nestedViewShortcode,
            deeplyNestedHtml
          );
        }

        // Procesar shortcodes de campos en la vista anidada
        const shortcodeRegex = /\[([^\]]+)\]/g;
        const matches = nestedHtml.match(shortcodeRegex) || [];

        for (const shortcode of matches) {
          const fieldKey = shortcode.slice(1, -1);
          const value = await this.getSyncValueForField(fieldKey);
          nestedHtml = nestedHtml.replace(shortcode, value);
        }

        // Envolver en un contenedor para identificar que es una vista anidada
        return nestedHtml;
      } catch (error) {
        console.error(`Error rendering nested view ${viewId}:`, error);
        return `<div class="nested-view-error">
          <i class="fas fa-exclamation-triangle"></i> Error al renderizar la vista: ${error.message}
        </div>`;
      }
    },
    async createShadowDOM(htmlContent) {
      const host = this.$refs.shadowHost;
      if (!host) return;

      // Verificar si ya existe un Shadow DOM
      if (host.shadowRoot) {
        // Si ya existe, solo actualizar el contenido
        this.shadowRoot = host.shadowRoot;
      } else {
        // Limpiar Shadow DOM anterior si existe
        this.clearShadowDOM();
        // Crear Shadow DOM
        this.shadowRoot = host.attachShadow({ mode: "open" });
      }

      // Cargar estilos compartidos desde la API (en prod /shared-styles.css no existe en el front estático)
      let sharedStyles = "";
      try {
        const baseUrl = (process.env.VUE_APP_WENCES_API_URL || "").replace(/\/$/, "") || window.location.origin;
        const url = `${baseUrl}/api/shared-styles.css`;
        const response = await fetch(url);
        if (response.ok) {
          sharedStyles = await response.text();
        }
      } catch (e) {
        console.warn("Could not load shared-styles.css:", e);
      }

      // Cargar CSS personalizado desde la API (client_default_css)
      let customCss = "";
      try {
        const { data } = await wencesApi.get("/api/settings");
        if (data.success && data.data && data.data.customCss) {
          customCss = data.data.customCss;
        }
      } catch (e) {
        console.warn("Could not load custom CSS:", e);
      }

      // Cargar estilos de Font Awesome (all.css) si es necesario
      let fontAwesomeStyles = "";
      try {
        const response = await fetch("https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.8.2/css/all.css");
        if (response.ok) {
          fontAwesomeStyles = await response.text();
        }
      } catch (e) {
        console.warn("Could not load Font Awesome styles:", e);
      }

      // Extraer y procesar estilos del HTML content (para variables CSS :root)
      let processedHtmlContent = htmlContent;
      let htmlStyles = "";
      
      // Buscar bloques <style> en el HTML
      const styleRegex = /<style[^>]*>([\s\S]*?)<\/style>/gi;
      const styleMatches = htmlContent.match(styleRegex);
      
      if (styleMatches) {
        styleMatches.forEach((styleTag) => {
          // Extraer el contenido del style
          const styleContent = styleTag.replace(/<style[^>]*>|<\/style>/gi, "");
          
          // Reemplazar :root por :host y .view-html-content para que funcione en Shadow DOM
          // :host es el elemento raíz del Shadow DOM, y .view-html-content es el contenedor
          // Esto asegura que las variables CSS estén disponibles en todo el Shadow DOM
          let processedStyle = styleContent;
          
          // Reemplazar :root con diferentes patrones
          processedStyle = processedStyle.replace(/:root\s*{/g, ":host, .view-html-content {");
          processedStyle = processedStyle.replace(/:root\s*,/g, ":host, .view-html-content,");
          processedStyle = processedStyle.replace(/:root\s+/g, ":host, .view-html-content ");
          
          htmlStyles += processedStyle + "\n";
          
          // Remover el bloque <style> del HTML content para evitar duplicados
          processedHtmlContent = processedHtmlContent.replace(styleTag, "");
        });
      }

      // Reset CSS completo para aislar del sistema
      const cssReset = `
        /* CSS Reset completo para aislamiento del sistema */
        :host {
          display: block;
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          background: white;
        }
        
        /* Reset básico de márgenes y padding */
        * {
          box-sizing: border-box;
        }
        
        /* Reset de fuentes y colores base */
        html, body {
          margin: 0;
          padding: 0;
          font-family: Arial, sans-serif;
          font-size: 14px;
          line-height: 1.5;
          color: #333;
          background: white;
        }
        
        /* Reset de elementos de texto */
        h1, h2, h3, h4, h5, h6 {
          margin: 0;
          padding: 0;
          font-weight: normal;
          font-size: inherit;
        }
        
        p {
          margin: 0 0 1em 0;
          padding: 0;
        }
        
        /* Reset de tablas */
        table {
          border-collapse: collapse;
          border-spacing: 0;
          width: 100%;
        }
        
        th, td {
          padding: 0;
          text-align: left;
        }
        
        /* Reset de listas */
        ul, ol {
          margin: 0;
          padding: 0;
          list-style: none;
        }
        
        li {
          margin: 0;
          padding: 0;
        }
        
        /* Reset de enlaces */
        a {
          text-decoration: none;
          color: inherit;
        }
        
        /* Reset de imágenes */
        img {
          max-width: 100%;
          height: auto;
          display: block;
        }
        
        /* Reset de formularios */
        input, textarea, select, button {
          font-family: inherit;
          font-size: inherit;
          line-height: inherit;
          margin: 0;
          padding: 0;
          border: none;
          background: none;
        }
        
        /* Reset de elementos de bloque */
        div, span, section, article, header, footer, nav, aside {
          margin: 0;
          padding: 0;
        }
        
        /* Asegurar que los estilos del sistema no se filtren */
        * {
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
      `;

      // Crear el contenido HTML completo con estilos aislados
      const fullHtml = `
        <style>
          ${cssReset}
          
          ${sharedStyles}
          
          ${fontAwesomeStyles}
          
          ${customCss}
          
          /* Estilos específicos del componente */
          .view-html-content {
            line-height: 1.6;
            font-family: Arial, sans-serif;
            color: #333;
            background: white;
          }
          
          /* Estilos extraídos del HTML content (con :root convertido a .view-html-content) */
          ${htmlStyles}
          
          .nested-view {
            border: 1px solid #dee2e6;
            padding: 10px;
            margin: 5px 0;
            border-radius: 4px;
            background-color: #f8f9fa;
            position: relative;
          }
          
          .nested-view-header {
            font-size: 0.8em;
            color: #6c757d;
            margin-bottom: 5px;
            border-bottom: 1px solid #dee2e6;
            padding-bottom: 3px;
            font-weight: bold;
          }
          
          .nested-view-content {
            padding-top: 5px;
          }
          
          .nested-view-error {
            border: 1px solid #dc3545;
            padding: 10px;
            margin: 5px 0;
            border-radius: 4px;
            background-color: #f8d7da;
            color: #721c24;
          }
          
          .nested-view .nested-view {
            margin-left: 15px;
            border-left: 3px solid #007bff;
          }

          .view-html-group-array-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0.75em 0;
            font-size: inherit;
          }
          .view-html-group-table-caption {
            caption-side: top;
            padding: 0 0 6px 0;
            font-weight: 600;
            color: #343a40;
          }
          .view-html-group-array-stacked-title {
            color: #343a40;
          }
          .view-html-group-array-table th,
          .view-html-group-array-table td {
            border: 1px solid #dee2e6;
            padding: 6px 10px;
            vertical-align: top;
            text-align: left;
          }
          .view-html-group-array-table thead th {
            background-color: #f1f3f5;
            font-weight: 600;
          }
          .view-html-group-array-table.view-html-group-array-table--no-header tbody tr:first-child td {
            border-top: 1px solid #dee2e6;
          }
          .view-html-group-array-stacked .group-container {
            margin-bottom: 0;
          }
          .view-html-group-array-stacked-row {
            margin-bottom: 1rem;
            padding-bottom: 0.75rem;
            border-bottom: 1px solid #e9ecef;
          }
          .view-html-group-array-stacked-row:last-child {
            margin-bottom: 0;
            padding-bottom: 0;
            border-bottom: none;
          }

          .view-html-group-nested {
            border: 1px solid #dee2e6;
            border-radius: 6px;
            padding: 8px 10px;
            background: linear-gradient(180deg, #fbfcfd 0%, #f4f6f8 100%);
            box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
          }
          .view-html-group-array-table td > .view-html-group-nested,
          .view-html-group-array-stacked-row .view-html-group-nested {
            padding: 6px 8px;
          }
          .view-html-group-nested .view-html-group-array-table {
            font-size: 0.92em;
            margin: 0.35em 0 0 0;
          }
          .view-html-group-nested .view-html-group-array-table th,
          .view-html-group-nested .view-html-group-array-table td {
            padding: 4px 8px;
          }

          .view-html-group-deep {
            font-size: 0.85em;
            line-height: 1.45;
            color: #444;
            padding: 8px 10px;
            background: #f8f9fa;
            border-radius: 6px;
            border-left: 3px solid #adb5bd;
          }
          .view-html-deep-row {
            margin-bottom: 5px;
          }
          .view-html-deep-row:last-child {
            margin-bottom: 0;
          }
          .view-html-deep-k {
            font-weight: 600;
            color: #495057;
          }
          .view-html-deep-v {
            color: #212529;
          }
        </style>
        <div class="view-html-content">
          ${processedHtmlContent}
        </div>
      `;

      // Actualizar el contenido del Shadow DOM
      if (this.shadowRoot) {
        this.shadowRoot.innerHTML = fullHtml;
      }
    },
    clearShadowDOM() {
      const host = this.$refs.shadowHost;
      if (host) {
        try {
          // Si ya existe un Shadow DOM, limpiar su contenido
          if (host.shadowRoot) {
            host.shadowRoot.innerHTML = "";
          }
          // Limpiar el contenido del host
          host.innerHTML = "";
        } catch (error) {
          console.warn("Error clearing shadow DOM:", error);
        }
        this.shadowRoot = null;
      }
    },
    /**
     * Parsea el interior de un shortcode de campo: `grupo`, `a>b>c`, `lineas:sku,qty`, `pedido>lineas:sku`.
     * Columnas del grupo final del path en el primer segmento.
     * Segmentos extra separados por `||`: `rutaCompleta>sub:col1,col2` (columnas por ruta anidada).
     */
    parseHtmlShortcodeFieldSpecMain(trimmed) {
      const t = String(trimmed || "").trim();
      if (t.startsWith("view:")) {
        return { rootKey: t, nestedPath: [], columnKeys: null };
      }
      const segments = t.split(">").map((s) => s.trim()).filter(Boolean);
      if (!segments.length) {
        return { rootKey: "", nestedPath: [], columnKeys: null };
      }
      const last = segments[segments.length - 1];
      let columnKeys = null;
      let lastKey = last;
      const colon = last.indexOf(":");
      if (colon !== -1) {
        lastKey = last.slice(0, colon).trim();
        const rest = last.slice(colon + 1).trim();
        if (rest) {
          columnKeys = rest
            .split(",")
            .map((c) => c.trim())
            .filter(Boolean);
        }
      }
      if (!lastKey) {
        return { rootKey: "", nestedPath: [], columnKeys };
      }
      const pathKeys = [...segments.slice(0, -1), lastKey];
      const rootKey = pathKeys[0];
      const nestedPath = pathKeys.slice(1);
      return {
        rootKey,
        nestedPath,
        columnKeys: columnKeys && columnKeys.length ? columnKeys : null,
      };
    },
    parseHtmlShortcodePathColumnMapFromExtras(fullSpec) {
      const pathColumnMap = {};
      const parts = String(fullSpec || "").split("||");
      for (let i = 1; i < parts.length; i++) {
        const ch = parts[i].trim();
        if (!ch || ch.toLowerCase().startsWith("df:")) continue;
        const lc = ch.lastIndexOf(":");
        if (lc === -1) continue;
        const path = ch.slice(0, lc).trim();
        const keys = ch
          .slice(lc + 1)
          .split(",")
          .map((x) => x.trim())
          .filter(Boolean);
        if (path && keys.length) pathColumnMap[path] = keys;
      }
      return Object.keys(pathColumnMap).length ? pathColumnMap : null;
    },
    parseHtmlShortcodeFieldSpec(spec) {
      const full = String(spec || "").trim();
      const mainPart = full.split("||")[0].trim();
      const base = this.parseHtmlShortcodeFieldSpecMain(mainPart);
      const pathColumnMap = this.parseHtmlShortcodePathColumnMapFromExtras(full);
      const dateFormat = parseHtmlShortcodeDateFormatFromExtras(full);
      return { ...base, pathColumnMap, dateFormat };
    },
    getHtmlDateFormatForPath(fieldPath) {
      if (!fieldPath) return null;
      const raw = this.view?.design?.htmlDateFormats?.[fieldPath];
      if (raw == null || String(raw).trim() === "") return null;
      return String(raw).trim();
    },
    formatHtmlShortcodeDateValue(value, dateFormat) {
      const basePath = this._shortcodeDateBasePath || "";
      const fmt = resolveHtmlShortcodeDateFormat({
        parsedDateFormat: dateFormat,
        viewDesign: this.view?.design,
        basePath,
      });
      return formatDateWithHtmlPattern(value, fmt, "");
    },
    pickGroupFieldsForColumns(field, columnKeys) {
      if (!field?.fields?.length) return [];
      if (!columnKeys || !columnKeys.length) return field.fields;
      const picked = columnKeys
        .map((k) => field.fields.find((f) => f.key === k))
        .filter(Boolean);
      return picked.length ? picked : field.fields;
    },
    /** Columnas por ruta de grupo en el diseño de la vista (incl. anidados: padre>hijo). */
    getHtmlGroupColumnKeysForPath(groupPath) {
      if (!groupPath) return null;
      const raw = this.view?.design?.htmlGroupColumnKeys?.[groupPath];
      if (raw == null) return null;
      const arr = Array.isArray(raw)
        ? raw
        : String(raw)
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean);
      return arr.length ? arr : null;
    },
    getHtmlGroupHideTableHeader(groupPath) {
      if (!groupPath) return false;
      return !!this.view?.design?.htmlGroupHideTableHeader?.[groupPath];
    },
    getHtmlGroupPanelLabelsEntry(groupPath) {
      if (!groupPath) return null;
      const raw = this.view?.design?.htmlGroupPanelLabels?.[groupPath];
      if (!raw || typeof raw !== "object") return null;
      return raw;
    },
    resolveHtmlGroupPanelColumnLabel(groupPath, fieldKey, modelLabel) {
      const fallback = modelLabel != null ? String(modelLabel) : "";
      let raw = fallback;
      if (groupPath && fieldKey) {
        const entry = this.getHtmlGroupPanelLabelsEntry(groupPath);
        const o = entry?.columnLabels?.[fieldKey];
        if (o != null && String(o).trim() !== "") raw = String(o).trim();
      }
      return this.translateStoredViewHtmlLabel(raw);
    },
    getHtmlGroupPanelCustomGroupTitle(groupPath) {
      if (!groupPath) return "";
      const entry = this.getHtmlGroupPanelLabelsEntry(groupPath);
      const o = entry?.groupTitle;
      if (o == null || String(o).trim() === "") return "";
      return this.translateStoredViewHtmlLabel(String(o).trim());
    },
    mergeColumnKeys(shortcodeKeys, groupPath) {
      if (shortcodeKeys != null && shortcodeKeys.length) return shortcodeKeys;
      const m = this._shortcodePathColumnMap;
      if (m && groupPath && m[groupPath] != null && m[groupPath].length) {
        return m[groupPath];
      }
      return this.getHtmlGroupColumnKeysForPath(groupPath);
    },
    async navigateGroupShortcodeNested(
      currentField,
      currentValue,
      pathIndex,
      nestedPath,
      columnKeys,
      shortcodeTargetPath
    ) {
      if (pathIndex >= nestedPath.length) {
        return null;
      }

      const currentPathPart = nestedPath[pathIndex];
      const subField = currentField.fields?.find((f) => f.key === currentPathPart);
      if (!subField) {
        console.warn(
          `Subcampo ${currentPathPart} no encontrado en el grupo ${currentField.key}`
        );
        return null;
      }

      if (pathIndex === nestedPath.length - 1) {
        const finalValue = currentValue[currentPathPart];

        if (subField.type === "group" && subField.fields) {
          if (subField.isArray && Array.isArray(finalValue)) {
            if (finalValue.length === 0) return "";
            return await this.renderGroupArrayHtml(
              subField,
              finalValue,
              0,
              columnKeys,
              shortcodeTargetPath
            );
          }
          if (
            !subField.isArray &&
            finalValue &&
            typeof finalValue === "object" &&
            !Array.isArray(finalValue)
          ) {
            return await this.renderGroupFieldsAsync(
              subField,
              finalValue,
              0,
              columnKeys,
              shortcodeTargetPath
            );
          }
          return "";
        }

        if (subField.isArray && Array.isArray(finalValue)) {
          if (subField.type === "reference" && subField.ref) {
            try {
              const valuePromises = finalValue.map(async (item) => {
                const { data } = await wencesApi.get(
                  `/api/gotrading/dynamic/${subField.ref}/${item}`
                );
                return data.name || data.label || data._id;
              });
              const values = await Promise.all(valuePromises);
              return values.join("<br>");
            } catch (error) {
              console.error(
                `Error loading references for array field ${currentPathPart}:`,
                error
              );
              return finalValue.join("<br>");
            }
          }
          const formattedValues = finalValue.map((item) =>
            this.formatSubFieldValue(subField, item)
          );
          return formattedValues.join("<br>");
        }

        if (subField.type === "reference" && subField.ref) {
          try {
            const { data } = await wencesApi.get(
              `/api/gotrading/dynamic/${subField.ref}/${finalValue}`
            );
            return data.name || data.label || data._id;
          } catch (error) {
            console.error(
              `Error loading reference for ${currentPathPart}:`,
              error
            );
            return finalValue || "";
          }
        }

        return this.formatSubFieldValue(subField, finalValue, shortcodeTargetPath);
      }

      if (subField.type === "group") {
        const subValue = currentValue[currentPathPart];

        if (Array.isArray(subValue)) {
          const results = [];
          for (let i = 0; i < subValue.length; i++) {
            const element = subValue[i];
            const result = await this.navigateGroupShortcodeNested(
              subField,
              element,
              pathIndex + 1,
              nestedPath,
              columnKeys,
              shortcodeTargetPath
            );
            if (result !== null && result !== "") {
              results.push(result);
            }
          }
          if (results.length > 0) {
            return results.join("<br>");
          }
        } else if (subValue) {
          return await this.navigateGroupShortcodeNested(
            subField,
            subValue,
            pathIndex + 1,
            nestedPath,
            columnKeys,
            shortcodeTargetPath
          );
        } else {
          console.warn(
            `Valor no encontrado para el grupo anidado ${currentPathPart}`
          );
          return "";
        }
      } else {
        console.warn(
          `Campo ${currentPathPart} no es un grupo, no se puede navegar más`
        );
        return "";
      }

      return null;
    },
    async getSyncValueForField(fieldSpec) {
      const specTrim = String(fieldSpec || "").trim();
      if (/^[a-z][\w]*:[a-z][\w]*$/i.test(specTrim)) {
        const modelName = this.view?.modelName;
        const rawId = this.value?._id;
        const itemId =
          rawId != null && typeof rawId === "object"
            ? rawId.toString?.() || String(rawId)
            : rawId != null
              ? String(rawId)
              : "";
        if (modelName && itemId) {
          try {
            const { data } = await wencesApi.post(
              "/api/gotrading/views/resolve-html-shortcode",
              {
                modelName,
                itemId,
                spec: specTrim,
                viewId: this.view?._id != null ? String(this.view._id) : undefined,
              }
            );
            return data?.html ?? "";
          } catch (err) {
            const msg =
              err?.response?.data?.message ||
              err?.message ||
              "Error al resolver shortcode";
            console.warn("[ViewHtml] shortcode módulo:", specTrim, msg);
            return "";
          }
        }
        return "";
      }

      const parsed = this.parseHtmlShortcodeFieldSpec(fieldSpec);
      const { rootKey: fieldKey, nestedPath, columnKeys, dateFormat } = parsed;
      this._shortcodePathColumnMap = parsed.pathColumnMap;
      this._shortcodeDateFormat = dateFormat;
      this._shortcodeDateBasePath = [fieldKey, ...nestedPath]
        .filter(Boolean)
        .join(">");
      try {
        return await this.getSyncValueForFieldBody(
          fieldKey,
          nestedPath,
          columnKeys,
          dateFormat
        );
      } finally {
        this._shortcodePathColumnMap = null;
        this._shortcodeDateFormat = null;
        this._shortcodeDateBasePath = null;
      }
    },
    async getSyncValueForFieldBody(fieldKey, nestedPath, columnKeys, dateFormat) {
      const propertyReferenceValue = nestedPath.length > 0;

      const value = this.value[fieldKey];
      const field = this.fields.find((f) => f.key === fieldKey);

      if (value === null || value === undefined) {
        return "";
      }

      // Campo tipo fecha (incl. createdAt en raíz del registro)
      if (isHtmlShortcodeDateField(fieldKey, field, nestedPath) && value) {
        const formatted = this.formatHtmlShortcodeDateValue(value, dateFormat);
        if (formatted) return formatted;
      }

      // Si no hay campo definido, intentar detectar si es un número y formatearlo
      if (!field) {
        if (value !== null && value !== undefined && value !== "") {
          // Intentar detectar si es un número
          const numValue = typeof value === "string" ? parseFloat(value) : value;
          if (!isNaN(numValue) && typeof numValue === "number") {
            // Detectar si es un campo number con currency activado para mostrar decimales
            const isPriceField = this.isPriceOrTotalField(fieldKey, null);
            return formatNumberSync(numValue, null, isPriceField);
          }
          return String(value);
        }
        return "";
      }

      // Campo tipo lista (mismo formato que model-definition-core/list/viewField.vue)
      if (field.type === "list") {
        if (field.isArray && Array.isArray(value)) {
          return value.map((v) => this.getListOptionLabel(field, v)).join("<br>");
        }
        return this.getListOptionLabel(field, value);
      }

      // Campo tipo referencia
      if (field.type === "reference") {
        const refModel = this.findModelByRef(field.ref);

        if (refModel && value) {
          // Manejar referencias anidadas
          if (propertyReferenceValue && nestedPath.length > 0) {
            // Manejar referencias anidadas de múltiples niveles
            let currentData = null;
            let currentModel = refModel;
            let currentValue = value._id || value;

            try {
              // Obtener los datos del primer nivel (client)
              const { data: firstLevelData } = await wencesApi.get(
                `/api/gotrading/dynamic/${currentModel.modelName}/${currentValue}`
              );
              currentData = firstLevelData;

              // Si solo hay un nivel (client>name), obtener el valor directamente
              if (nestedPath.length === 1) {
                const finalValue = currentData[nestedPath[0]];
                const finalField = currentModel.fields.find(
                  (f) => f.key === nestedPath[0]
                );

                if (finalValue) {
                  switch (finalField?.type) {
                    case "date": {
                      const formatted = this.formatHtmlShortcodeDateValue(
                        finalValue,
                        dateFormat
                      );
                      return formatted || finalValue;
                    }
                    case "list":
                      return finalValue.label || finalValue;
                    case "number":
                      if (finalField.currency) {
                        return this.formatCurrencyValue(finalField, finalValue, currentData);
                      }
                      return formatNumberSync(finalValue);
                    default:
                      return finalValue;
                  }
                }
                return "";
              }

              // Si hay múltiples niveles, navegar por cada uno
              for (let i = 0; i < nestedPath.length; i++) {
                const pathPart = nestedPath[i];

                // Si no es el último nivel, preparar para el siguiente
                if (i < nestedPath.length - 1) {
                  const nextPathPart = nestedPath[i + 1];
                  const nextField = currentModel.fields.find(
                    (f) => f.key === pathPart
                  );

                  if (nextField && nextField.type === "reference") {
                    // Buscar el modelo de referencia para el siguiente nivel
                    const nextModel = this.findModelByRef(nextField.ref);
                    if (nextModel) {
                      currentModel = nextModel;
                      currentValue =
                        currentData[pathPart]?._id || currentData[pathPart];

                      // Obtener los datos del siguiente nivel
                      const { data } = await wencesApi.get(
                        `/api/gotrading/dynamic/${currentModel.modelName}/${currentValue}`
                      );
                      currentData = data;
                    } else {
                      console.warn(
                        `Model not found for reference: ${nextField.ref}`
                      );
                      return "";
                    }
                  } else {
                    console.warn(`Field ${pathPart} is not a reference type`);
                    return "";
                  }
                }
              }

              // Obtener el valor final del último nivel
              const finalValue = currentData[nestedPath[nestedPath.length - 1]];
              const finalField = currentModel.fields.find(
                (f) => f.key === nestedPath[nestedPath.length - 1]
              );

              if (finalValue) {
                switch (finalField?.type) {
                  case "date": {
                    const formatted = this.formatHtmlShortcodeDateValue(
                      finalValue,
                      dateFormat
                    );
                    return formatted || finalValue;
                  }
                  case "list":
                    return finalValue.label || finalValue;
                  case "number":
                    if (finalField.currency) {
                      return this.formatCurrencyValue(finalField, finalValue, currentData);
                    }
                    return formatNumberSync(finalValue);
                  default:
                    return finalValue;
                }
              }

              return "";
            } catch (error) {
              console.error(
                `Error navigating nested reference path: ${nestedPath.join(
                  ">"
                )}`,
                error
              );
              return "";
            }
          } else {
          }

          // Si el valor es un objeto con _id, buscar en referenceOptions
          if (typeof value === "object" && value._id) {
            return value.name;
          }
          // Si el valor es un string (el _id), cargar bajo demanda
          else if (typeof value === "string") {
            try {
              const { data } = await wencesApi.get(
                `/api/gotrading/dynamic/${field.ref}/${value}`
              );
              return data.name || data.label || data._id;
            } catch (error) {
              console.error(`Error loading reference for ${fieldKey}:`, error);
              return value;
            }
          }
          return String(value);
        }
      }

      // Campo tipo grupo
      if (field.type === "group") {
        const shortcodeTargetPath = [fieldKey, ...nestedPath].join(">");
        if (propertyReferenceValue && nestedPath.length > 0) {
          try {
            if (field.isArray && Array.isArray(value) && value.length > 0) {
              const results = [];
              for (let i = 0; i < value.length; i++) {
                const result = await this.navigateGroupShortcodeNested(
                  field,
                  value[i],
                  0,
                  nestedPath,
                  columnKeys,
                  shortcodeTargetPath
                );
                if (result !== null && result !== "") {
                  results.push(result);
                }
              }
              if (results.length > 0) {
                return results.join("<br>");
              }
            } else {
              const result = await this.navigateGroupShortcodeNested(
                field,
                value,
                0,
                nestedPath,
                columnKeys,
                shortcodeTargetPath
              );
              if (result !== null && result !== "") {
                return result;
              }
            }
          } catch (error) {
            console.error(
              `Error navigating nested group path: ${nestedPath.join(">")}`,
              error
            );
            return "";
          }
        }

        if (field.isArray && Array.isArray(value)) {
          return await this.renderGroupArrayHtml(
            field,
            value,
            0,
            columnKeys,
            field.key
          );
        }
        return await this.renderGroupFieldsAsync(
          field,
          value,
          0,
          columnKeys,
          field.key
        );
      }

      // Campo tipo array
      if (field.type === "array") {
        return Array.isArray(value) ? value.join(", ") : "";
      }

      // Campo tipo imagen
      if (field.type === "image") {
        if (Array.isArray(value)) {
          return value.length > 0 ? `${value.length} imagen(es)` : "Sin imagen";
        }
        return value ? "Imagen seleccionada" : "Sin imagen";
      }

      // Campo tipo select
      if (
        field.type === "select" &&
        field.options &&
        Array.isArray(field.options)
      ) {
        const option = field.options.find((opt) => opt.value === value);
        return option ? option.label : String(value);
      }

      // Campo tipo archivo
      if (field.type === "file") {
        return value ? "Archivo adjunto" : "Sin archivo";
      }

      // Campo tipo número con formato de moneda
      if (
        field.type === "number" &&
        field.currency &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return this.formatCurrencyValue(field, value, this.value);
      }

      // Campo tipo número sin formato de moneda
      if (field.type === "number" || field.type === "currency") {
        if (value === null || value === undefined || value === "") {
          return "";
        }
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(numValue)) {
          return "";
        }
        // Detectar si es un campo number con currency activado para mostrar decimales
        const isPriceField = this.isPriceOrTotalField(field?.key || fieldKey, field);
        // Formato de número usando el formato configurado
        return formatNumberSync(numValue, null, isPriceField);
      }

      // Para cualquier otro tipo, convertir a string
      if (value) {
        return String(value);
      }
      return "";
    },
    async getValueForField(fieldSpec) {
      const parsed = this.parseHtmlShortcodeFieldSpec(fieldSpec);
      const { rootKey: fieldKey, nestedPath, columnKeys, dateFormat } = parsed;
      this._shortcodePathColumnMap = parsed.pathColumnMap;
      this._shortcodeDateFormat = dateFormat;
      this._shortcodeDateBasePath = [fieldKey, ...nestedPath]
        .filter(Boolean)
        .join(">");
      try {
        return await this.getValueForFieldBody(
          fieldKey,
          nestedPath,
          columnKeys,
          dateFormat
        );
      } finally {
        this._shortcodePathColumnMap = null;
        this._shortcodeDateFormat = null;
        this._shortcodeDateBasePath = null;
      }
    },
    async getValueForFieldBody(fieldKey, nestedPath, columnKeys, dateFormat) {
      const propertyReferenceValue = nestedPath.length > 0;

      const value = this.value[fieldKey];
      const field = this.fields.find((f) => f.key === fieldKey);

      if (value === null || value === undefined) {
        return "";
      }

      // Campo tipo fecha (incl. createdAt en raíz del registro)
      if (isHtmlShortcodeDateField(fieldKey, field, nestedPath) && value) {
        const formatted = this.formatHtmlShortcodeDateValue(value, dateFormat);
        if (formatted) return formatted;
      }

      // Si no hay campo definido, intentar detectar si es un número y formatearlo
      if (!field) {
        if (value !== null && value !== undefined && value !== "") {
          // Intentar detectar si es un número
          const numValue = typeof value === "string" ? parseFloat(value) : value;
          if (!isNaN(numValue) && typeof numValue === "number") {
            // Detectar si es un campo number con currency activado para mostrar decimales
            const isPriceField = this.isPriceOrTotalField(fieldKey, null);
            return formatNumberSync(numValue, null, isPriceField);
          }
          return String(value);
        }
        return "";
      }

      // Campo tipo lista (mismo formato que model-definition-core/list/viewField.vue)
      if (field.type === "list") {
        if (field.isArray && Array.isArray(value)) {
          return value.map((v) => this.getListOptionLabel(field, v)).join("<br>");
        }
        return this.getListOptionLabel(field, value);
      }

      // Campo tipo referencia
      if (field.type === "reference") {
        const refModel = this.findModelByRef(field.ref);
        if (refModel && value) {
          // Manejar referencias anidadas
          if (propertyReferenceValue && nestedPath.length > 0) {
            // Manejar referencias anidadas de múltiples niveles
            let currentData = null;
            let currentModel = refModel;
            let currentValue = value._id || value;

            try {
              // Navegar por cada nivel de anidación
              for (let i = 0; i < nestedPath.length; i++) {
                const pathPart = nestedPath[i];

                // Obtener los datos del nivel actual
                const { data } = await wencesApi.get(
                  `/api/gotrading/dynamic/${currentModel.modelName}/${currentValue}`
                );
                currentData = data;

                // Si no es el último nivel, preparar para el siguiente
                if (i < nestedPath.length - 1) {
                  const nextPathPart = nestedPath[i + 1];
                  const nextField = currentModel.fields.find(
                    (f) => f.key === pathPart
                  );

                  if (nextField && nextField.type === "reference") {
                    // Buscar el modelo de referencia para el siguiente nivel
                    const nextModel = this.findModelByRef(nextField.ref);
                    if (nextModel) {
                      currentModel = nextModel;
                      currentValue =
                        currentData[pathPart]?._id || currentData[pathPart];
                    } else {
                      console.warn(
                        `Model not found for reference: ${nextField.ref}`
                      );
                      return "";
                    }
                  } else {
                    console.warn(`Field ${pathPart} is not a reference type`);
                    return "";
                  }
                }
              }

              // Obtener el valor final del último nivel
              if (nestedPath[nestedPath.length - 1]) {
                finalValue = currentData[nestedPath[nestedPath.length - 1]];
                finalField = currentModel.fields.find(
                  (f) => f.key === nestedPath[nestedPath.length - 1]
                );
              }

              if (finalValue) {
                switch (finalField?.type) {
                  case "date": {
                    const formatted = this.formatHtmlShortcodeDateValue(
                      finalValue,
                      dateFormat
                    );
                    return formatted || finalValue;
                  }
                  case "list":
                    return finalValue.label || finalValue;
                  case "number":
                    if (finalField.currency) {
                      return this.formatCurrencyValue(finalField, finalValue, currentData);
                    }
                    return formatNumberSync(finalValue);
                  default:
                    return finalValue;
                }
              }

              return "";
            } catch (error) {
              console.error(
                `Error navigating nested reference path: ${nestedPath.join(
                  ">"
                )}`,
                error
              );
              return "";
            }
          }

          // Si el valor es un objeto con _id, buscar en referenceOptions
          if (typeof value === "object" && value._id) {
            return value.name;
          }
          // Si el valor es un string (el _id), buscar en referenceOptions
          else if (typeof value === "string") {
            const refOptions = this.referenceOptions[field.key];
            if (refOptions) {
              const refItem = refOptions.find((item) => item._id === value);
              return refItem
                ? refItem.name || refItem.label || refItem._id
                : value;
            }
          }
          return String(value);
        }
      }

      // Campo tipo grupo
      if (field.type === "group") {
        const shortcodeTargetPath = [fieldKey, ...nestedPath].join(">");
        if (propertyReferenceValue && nestedPath.length > 0) {
          try {
            if (field.isArray && Array.isArray(value) && value.length > 0) {
              const results = [];
              for (let i = 0; i < value.length; i++) {
                const result = await this.navigateGroupShortcodeNested(
                  field,
                  value[i],
                  0,
                  nestedPath,
                  columnKeys,
                  shortcodeTargetPath
                );
                if (result !== null && result !== "") {
                  results.push(result);
                }
              }
              if (results.length > 0) {
                return results.join("<br>");
              }
            } else {
              const result = await this.navigateGroupShortcodeNested(
                field,
                value,
                0,
                nestedPath,
                columnKeys,
                shortcodeTargetPath
              );
              if (result !== null && result !== "") {
                return result;
              }
            }
          } catch (error) {
            console.error(
              `Error navigating nested group path: ${nestedPath.join(">")}`,
              error
            );
            return "";
          }
        }

        if (field.isArray && Array.isArray(value)) {
          return await this.renderGroupArrayHtml(
            field,
            value,
            0,
            columnKeys,
            field.key
          );
        }
        return await this.renderGroupFieldsAsync(
          field,
          value,
          0,
          columnKeys,
          field.key
        );
      }

      // Campo tipo array
      if (field.type === "array") {
        return Array.isArray(value) ? value.join(", ") : "";
      }

      // Campo tipo imagen
      if (field.type === "image") {
        if (Array.isArray(value)) {
          return value.length > 0 ? `${value.length} imagen(es)` : "Sin imagen";
        }
        return value ? "Imagen seleccionada" : "Sin imagen";
      }

      // Campo tipo select
      if (
        field.type === "select" &&
        field.options &&
        Array.isArray(field.options)
      ) {
        const option = field.options.find((opt) => opt.value === value);
        return option ? option.label : String(value);
      }

      // Campo tipo archivo
      if (field.type === "file") {
        return value ? "Archivo adjunto" : "Sin archivo";
      }

      // Campo tipo número con formato de moneda
      if (
        field.type === "number" &&
        field.currency &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        return this.formatCurrencyValue(field, value, this.value);
      }

      // Campo tipo número sin formato de moneda
      if (field.type === "number" || field.type === "currency") {
        if (value === null || value === undefined || value === "") {
          return "";
        }
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(numValue)) {
          return "";
        }
        // Detectar si es un campo number con currency activado para mostrar decimales
        const isPriceField = this.isPriceOrTotalField(field?.key || fieldKey, field);
        // Formato de número usando el formato configurado
        return formatNumberSync(numValue, null, isPriceField);
      }

      // Para cualquier otro tipo, convertir a string
      if (value) {
        return String(value);
      }
      return "";
    },
    escapeHtmlText(str) {
      if (str == null) return "";
      return String(str)
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;");
    },
    /** Vista HTML: cómo pintar un grupo con isArray. Prioridad: diseño de la vista → definición del campo → tabla. */
    getHtmlGroupArrayFormat(field) {
      if (!field || !field.key) return "table";
      const fromView = this.view?.design?.htmlGroupArrayFormats?.[field.key];
      if (fromView === "stacked" || fromView === "table") return fromView;
      const fromField = field.htmlGroupArrayFormat;
      if (fromField === "stacked" || fromField === "table") return fromField;
      return "table";
    },
    /**
     * Profundidad de grupo en vista HTML: 0 = grupo del shortcode [x];
     * 1 = un subgrupo (único anidamiento “rico”); ≥ 2 = segundo anidamiento → lista compacta.
     */
    isRichGroupNestDepth(groupNestDepth) {
      return groupNestDepth < 2;
    },
    async formatSubFieldDisplayHtmlAsync(
      subField,
      subValue,
      groupNestDepth = 0,
      groupPath = null
    ) {
      if (subField.type === "group") {
        if (!this.isRichGroupNestDepth(groupNestDepth)) {
          return this.renderGroupFlatLinesAsync(subField, subValue);
        }
        return this.renderNestedGroupShallowAsync(
          subField,
          subValue,
          groupNestDepth,
          groupPath
        );
      }

      if (subField.type === "reference" && subField.ref) {
        if (subValue === null || subValue === undefined || subValue === "") {
          return "-";
        }
        if (typeof subValue === "object" && subValue._id) {
          const refOptions = this.referenceOptions[subField.key];
          if (refOptions) {
            const refItem = refOptions.find((item) => item._id === subValue._id);
            if (refItem) {
              return refItem.name || refItem.label || refItem._id || "-";
            }
          }
          return subValue.name || subValue.label || subValue._id || "-";
        }
        try {
          const { data } = await wencesApi.get(
            `/api/gotrading/dynamic/${subField.ref}/${subValue}`
          );
          return data.name || data.label || data._id || "-";
        } catch (error) {
          console.error(`Error loading reference for ${subField.key}:`, error);
          return "-";
        }
      }
      return this.formatSubFieldValue(subField, subValue);
    },
    /** Un solo nivel de subgrupo con tabla/stacked; el contenido interno usa groupNestDepth + 1. */
    async renderNestedGroupShallowAsync(
      subField,
      subValue,
      groupNestDepth,
      groupPath = null
    ) {
      if (!subField.fields || !Array.isArray(subField.fields)) return "-";
      if (subValue === null || subValue === undefined) return "-";

      const nextDepth = groupNestDepth + 1;
      let inner;
      if (subField.isArray && Array.isArray(subValue)) {
        if (subValue.length === 0) return "-";
        inner = await this.renderGroupArrayHtml(
          subField,
          subValue,
          nextDepth,
          null,
          groupPath
        );
      } else if (typeof subValue === "object" && !Array.isArray(subValue)) {
        inner = await this.renderGroupFieldsAsync(
          subField,
          subValue,
          nextDepth,
          null,
          groupPath
        );
      } else {
        return "-";
      }

      return `<div class="view-html-group-nested" data-group-nest="${groupNestDepth}">${inner}</div>`;
    },
    /** Tercer nivel de grupo en adelante: lista plana etiqueta · valor (sin más tablas). */
    async renderGroupFlatLinesAsync(field, value) {
      if (value === null || value === undefined) return "-";
      const lines = await this.collectFlatGroupLines(field, value, "");
      if (!lines.length) return "-";
      const rows = lines
        .map(
          (line) =>
            `<div class="view-html-deep-row"><span class="view-html-deep-k">${this.escapeHtmlText(line.k)}</span> · <span class="view-html-deep-v">${line.v}</span></div>`
        )
        .join("");
      return `<div class="view-html-group-deep">${rows}</div>`;
    },
    async collectFlatGroupLines(field, value, prefix) {
      if (!field?.fields || !Array.isArray(field.fields)) return [];
      if (value === null || value === undefined) return [];

      if (field.isArray && Array.isArray(value)) {
        if (value.length === 0) return [];
        const out = [];
        const base = field.label || field.key || "Línea";
        for (let i = 0; i < value.length; i++) {
          const rowPrefix = `${prefix}${base} (${i + 1}) › `;
          out.push(
            ...(await this.collectFlatGroupLinesSingleRow(
              field,
              value[i],
              rowPrefix
            ))
          );
        }
        return out;
      }

      if (typeof value === "object" && !Array.isArray(value)) {
        return this.collectFlatGroupLinesSingleRow(field, value, prefix);
      }
      return [];
    },
    async collectFlatGroupLinesSingleRow(field, rowObj, prefix) {
      const out = [];
      if (!rowObj || typeof rowObj !== "object") return out;

      for (const sub of field.fields) {
        const v = rowObj[sub.key];
        const label = `${prefix}${sub.label || sub.key}`;

        if (sub.type === "group" && sub.fields) {
          if (v === null || v === undefined) continue;
          if (sub.isArray && Array.isArray(v)) {
            out.push(...(await this.collectFlatGroupLines(sub, v, `${label} › `)));
          } else if (typeof v === "object" && !Array.isArray(v)) {
            out.push(
              ...(await this.collectFlatGroupLines(sub, v, `${label} › `))
            );
          }
          continue;
        }

        const display = await this.formatSubFieldDisplayHtmlAsync(sub, v, 2);
        out.push({ k: label, v: display });
      }
      return out;
    },
    async renderGroupArrayHtml(
      field,
      values,
      groupNestDepth = 0,
      columnKeys = null,
      groupPath = null
    ) {
      const effectiveKeys = this.mergeColumnKeys(columnKeys, groupPath);
      const fmt = this.getHtmlGroupArrayFormat(field);
      if (fmt === "stacked") {
        return this.renderGroupArrayStacked(
          field,
          values,
          groupNestDepth,
          effectiveKeys,
          groupPath
        );
      }
      return this.renderGroupArrayTable(
        field,
        values,
        groupNestDepth,
        effectiveKeys,
        groupPath
      );
    },
    async renderGroupArrayStacked(
      field,
      values,
      groupNestDepth = 0,
      columnKeys = null,
      groupPath = null
    ) {
      if (!field.fields || !Array.isArray(values) || values.length === 0) {
        return "";
      }
      const blocks = await Promise.all(
        values.map((row) =>
          this.renderGroupFieldsAsync(
            field,
            row,
            groupNestDepth,
            columnKeys,
            groupPath
          )
        )
      );
      const fk = this.escapeHtmlText(field.key);
      const customTitle = this.getHtmlGroupPanelCustomGroupTitle(groupPath);
      const titleBlock =
        customTitle !== ""
          ? `<div class="view-html-group-array-stacked-title small fw-semibold mb-1">${this.escapeHtmlText(
              customTitle
            )}</div>`
          : "";
      return `<div class="view-html-group-array-stacked" data-field-key="${fk}">${titleBlock}${blocks
        .map(
          (html) =>
            `<div class="view-html-group-array-stacked-row">${html}</div>`
        )
        .join("")}</div>`;
    },
    async renderGroupFieldsAsync(
      field,
      value,
      groupNestDepth = 0,
      columnKeys = null,
      groupPath = null
    ) {
      if (!field.fields || !value) return "";
      const effectiveKeys = this.mergeColumnKeys(columnKeys, groupPath);
      const cols = this.pickGroupFieldsForColumns(field, effectiveKeys);
      const parts = await Promise.all(
        cols.map(async (subField) => {
          const childPath = groupPath
            ? `${groupPath}>${subField.key}`
            : subField.key;
          const displayValue = await this.formatSubFieldDisplayHtmlAsync(
            subField,
            value[subField.key],
            groupNestDepth,
            childPath
          );
          const lab = this.escapeHtmlText(
            this.resolveHtmlGroupPanelColumnLabel(
              groupPath,
              subField.key,
              subField.label || subField.key
            )
          );
          return `<div class="group-field"><strong>${lab}:</strong> ${displayValue}</div>`;
        })
      );
      return `<div class="group-container">${parts.join("")}</div>`;
    },
    renderGroupFields(field, value) {
      if (!field.fields || !value) return "";

      const fieldsHtml = field.fields
        .map((subField) => {
          const subValue = value[subField.key];
          const displayValue = this.formatSubFieldValue(subField, subValue);
          const lab = this.escapeHtmlText(subField.label);
          return `<div class="group-field">
          <strong>${lab}:</strong> ${displayValue}
        </div>`;
        })
        .join("");

      return `<div class="group-container">${fieldsHtml}</div>`;
    },
    async renderGroupArrayTable(
      field,
      values,
      groupNestDepth = 0,
      columnKeys = null,
      groupPath = null
    ) {
      if (!field.fields || !Array.isArray(values)) return "";

      const cols = this.pickGroupFieldsForColumns(field, columnKeys);
      const hideHeader =
        groupPath && this.getHtmlGroupHideTableHeader(groupPath);
      const customTitle = this.getHtmlGroupPanelCustomGroupTitle(groupPath);
      const captionHtml =
        customTitle !== ""
          ? `<caption class="view-html-group-table-caption text-start">${this.escapeHtmlText(
              customTitle
            )}</caption>`
          : "";
      const headerRow = `<tr>${cols
        .map((f) => {
          const lab = this.resolveHtmlGroupPanelColumnLabel(
            groupPath,
            f.key,
            f.label || f.key
          );
          return `<th scope="col">${this.escapeHtmlText(lab)}</th>`;
        })
        .join("")}</tr>`;

      const rowPromises = values.map(async (rowObj) => {
        const cellPromises = cols.map(async (subField) => {
          const childPath = groupPath
            ? `${groupPath}>${subField.key}`
            : subField.key;
          const displayValue = await this.formatSubFieldDisplayHtmlAsync(
            subField,
            rowObj[subField.key],
            groupNestDepth,
            childPath
          );
          return `<td>${displayValue}</td>`;
        });
        const cells = await Promise.all(cellPromises);
        return `<tr>${cells.join("")}</tr>`;
      });

      const bodyRows = await Promise.all(rowPromises);
      const fk = this.escapeHtmlText(field.key);
      const tableClass =
        "view-html-group-array-table" +
        (hideHeader ? " view-html-group-array-table--no-header" : "");
      const headHtml = hideHeader ? "" : `<thead>${headerRow}</thead>`;
      return `<table class="${tableClass}" data-field-key="${fk}">${captionHtml}${headHtml}<tbody>${bodyRows.join("")}</tbody></table>`;
    },
    formatSubFieldValue(field, value, groupChildPath) {
      // IMPORTANTE: Los valores null/undefined se muestran como "-" en lugar de "null"
      if (value === null || value === undefined) return "-";

      // Campo tipo fecha (shortcode con formato o createdAt)
      if (isHtmlShortcodeDateField(field?.key, field) && value) {
        const scPath = this._shortcodeDateBasePath;
        if (scPath && groupChildPath && groupChildPath === scPath) {
          const formatted = this.formatHtmlShortcodeDateValue(
            value,
            this._shortcodeDateFormat
          );
          if (formatted) return formatted;
        }
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });
        }
      }

      // Campo tipo lista (mismo formato que model-definition-core/list/viewField.vue)
      if (field.type === "list") {
        if (field.isArray && Array.isArray(value)) {
          return value.map((v) => this.getListOptionLabel(field, v)).join("<br>");
        }
        return this.getListOptionLabel(field, value);
      }

      // Campo tipo referencia
      if (field.type === "reference") {
        const refModel = this.findModelByRef(field.ref);
        if (refModel && value) {
          if (typeof value === "object" && value._id) {
            const refOptions = this.referenceOptions[field.key];
            if (refOptions) {
              const refItem = refOptions.find((item) => item._id === value._id);
              return refItem
                ? refItem.name || refItem.label || refItem._id
                : value._id;
            }
          } else if (typeof value === "string") {
            const refOptions = this.referenceOptions[field.key];
            if (refOptions) {
              const refItem = refOptions.find((item) => item._id === value);
              return refItem
                ? refItem.name || refItem.label || refItem._id
                : value;
            }
          }
        }
      }

      // Campo tipo grupo (rutas síncronas; sin resolución async de referencias)
      if (field.type === "group") {
        if (value === null || value === undefined) return "-";
        if (field.isArray && Array.isArray(value)) {
          if (value.length === 0) return "-";
          const rows = value
            .map((row) => this.renderGroupFields(field, row))
            .filter(Boolean);
          return rows.length ? rows.join("") : "-";
        }
        if (typeof value === "object" && !Array.isArray(value)) {
          return this.renderGroupFields(field, value) || "-";
        }
        return "-";
      }

      // Campo tipo array
      if (field.type === "array") {
        return Array.isArray(value) ? value.join(", ") : "";
      }

      // Campo tipo imagen
      if (field.type === "image") {
        return value ? "Imagen seleccionada" : "Sin imagen";
      }

      // Campo tipo archivo
      if (field.type === "file") {
        return value ? "Archivo adjunto" : "Sin archivo";
      }

      // Campo tipo número con formato de moneda
      if (
        field.type === "number" &&
        field.currency &&
        value !== null &&
        value !== undefined &&
        value !== ""
      ) {
        // Para subcampos, usar el valor del grupo como contexto
        return this.formatCurrencyValue(field, value, this.value);
      }

      // Campo tipo número sin formato de moneda
      if (field.type === "number" || field.type === "currency") {
        if (value === null || value === undefined || value === "") {
          return "-";
        }
        const numValue = typeof value === "string" ? parseFloat(value) : value;
        if (isNaN(numValue)) {
          return "-";
        }
        // Detectar si es un campo number con currency activado para mostrar decimales
        const isPriceField = this.isPriceOrTotalField(field?.key, field);
        // Formato de número usando el formato configurado
        return formatNumberSync(numValue, null, isPriceField);
      }

      if (value) {
        return String(value);
      }
      return "";
    },
    formatCurrencyValue(field, value, item = null) {
      // Formatear campos de tipo number con currency = true
      if (value === null || value === undefined || value === "") {
        return "";
      }

      const numValue = typeof value === "string" ? parseFloat(value) : value;
      if (isNaN(numValue)) {
        return "";
      }

      // Formato usando el formato configurado - siempre mostrar decimales para currency
      let formatted = formatNumberSync(numValue, null, true);
      
      // Debug para valores específicos del template proforma
      if (numValue === 177289 || numValue === 9.33 || numValue === 124102.30 || numValue === 177289.00) {
        console.log('💰 formatCurrencyValue DEBUG:', {
          value: numValue,
          formatted: formatted,
          field: field?.key,
          formatFromCache: localStorage.getItem("numberFormat")
        });
      }

      // Moneda fija del campo (p. ej. total de gastos en la moneda del sistema, #397).
      if (field.currencyFixed) {
        const currencyList = (this.lists || []).find((l) => l && l.type === "currency");
        const fixed = currencyList && (currencyList.values || []).find((v) => String(v._id) === String(field.currencyFixed));
        return fixed && fixed.label ? `${formatted} ${fixed.label}` : formatted;
      }

      // Agregar símbolo de moneda si existe
      if (field.currencyField && item && item[field.currencyField]) {
        const currency = item[field.currencyField];
        const symbol = currency?.label || currency?.name || currency;
        if (symbol) {
          formatted = `${formatted} ${symbol}`;
        }
      }

      return formatted;
    },
    /**
     * Detecta si un campo es de tipo number con currency activado
     * Consulta la definición del modelo para verificar el tipo y la propiedad currency
     * @param {string} fieldKey - La clave del campo
     * @param {Object} field - El objeto field si está disponible (opcional)
     * @returns {boolean} - true si es un campo number con currency activado
     */
    isPriceOrTotalField(fieldKey, field = null) {
      if (!fieldKey) return false;
      
      // Si ya tenemos el objeto field, verificar directamente
      if (field) {
        return field.type === "number" && field.currency === true;
      }
      
      // Buscar el campo en this.fields (definición del modelo actual)
      const foundField = this.fields.find(f => f.key === fieldKey);
      if (foundField) {
        return foundField.type === "number" && foundField.currency === true;
      }
      
      // Obtener el modelName del view
      const modelName = this.view?.modelName;
      if (!modelName) return false;
      
      // Buscar la definición del modelo en this.models
      const modelDefinition = this.models.find(m => m.modelName === modelName);
      if (modelDefinition && modelDefinition.fields) {
        // Buscar el campo en la definición del modelo
        const modelField = this.findFieldInDefinition(modelDefinition.fields, fieldKey);
        if (modelField) {
          return modelField.type === "number" && modelField.currency === true;
        }
      }
      
      // Si no se encuentra, retornar false
      return false;
    },
    /**
     * Busca un campo en la definición del modelo, incluyendo campos anidados en grupos
     * @param {Array} fields - Array de campos de la definición
     * @param {string} fieldKey - La clave del campo a buscar
     * @returns {Object|null} - El campo encontrado o null
     */
    findFieldInDefinition(fields, fieldKey) {
      if (!fields || !Array.isArray(fields)) return null;
      
      // Buscar directamente en los campos
      const directField = fields.find(f => f.key === fieldKey);
      if (directField) return directField;
      
      // Buscar en campos anidados de grupos
      for (const field of fields) {
        if (field.type === "group" && field.fields && Array.isArray(field.fields)) {
          const foundInGroup = this.findFieldInDefinition(field.fields, fieldKey);
          if (foundInGroup) return foundInGroup;
        }
      }
      
      return null;
    },
    /** Opciones de la lista para un campo (mismo origen que list/viewField.vue) */
    getListOptionsForField(field) {
      if (!field || !field.listType) return [];
      const list = this.lists?.find((l) => l.type === field.listType);
      return list && list.values ? list.values : [];
    },
    /** Jerarquía Padre > Hijo para listas (misma lógica que list/viewField.vue buildHierarchy) */
    buildListHierarchy(itemId, listOptions, visited = new Set()) {
      if (!itemId || visited.has(String(itemId))) return [];
      visited.add(String(itemId));
      const item = listOptions.find((opt) => {
        const optId = opt._id || opt.id;
        return optId && optId.toString() === String(itemId);
      });
      if (!item || !item.label) return [];
      const hierarchy = [String(item.label)];
      if (item.parent) {
        const parentId = typeof item.parent === "object" && item.parent.toString
          ? item.parent.toString()
          : String(item.parent);
        const parentHierarchy = this.buildListHierarchy(parentId, listOptions, visited);
        return [...parentHierarchy, ...hierarchy];
      }
      return hierarchy;
    },
    /** Etiqueta de un valor de lista (misma lógica que list/viewField.vue getDisplayLabel) */
    getListOptionLabel(field, value) {
      if (value !== null && value !== undefined && value !== "") {
        const listOptions = this.getListOptionsForField(field);
        return this.getDisplayLabelForList(value, listOptions);
      }
      return "-";
    },
    getDisplayLabelForList(value, listOptions) {
      if (!value) return "";
      if (typeof value === "object" && value !== null) {
        if (value.hierarchy && typeof value.hierarchy === "string") return value.hierarchy;
        if (value.parent && listOptions.length > 0) {
          const hierarchy = this.buildListHierarchy(value._id || value.id, listOptions);
          if (hierarchy.length > 1) return hierarchy.join(" > ");
          if (hierarchy.length === 1) return hierarchy[0];
        }
        if (value.label !== undefined && value.label !== null) return String(value.label);
        if (value._id) {
          const option = listOptions.find((opt) => {
            const optId = opt._id || opt.id;
            return optId && optId.toString() === String(value._id);
          });
          if (option) return this.getDisplayLabelForList(option, listOptions);
        }
      }
      const valueId = typeof value === "object" ? value._id || value.id : value;
      if (valueId && listOptions.length > 0) {
        const option = listOptions.find((opt) => {
          const optId = opt._id || opt.id;
          return optId && optId.toString() === String(valueId);
        });
        if (option) return this.getDisplayLabelForList(option, listOptions);
      }
      return String(valueId || value || "");
    },
    async exportToExcelFromShadowDOM(element, fileName, fieldMapping) {
      try {
        // Extraer datos directamente del elemento del Shadow DOM
        const data = this.extractDataFromShadowDOM(element, fieldMapping);

        if (data.length > 0) {
          // Usar el método de exportación de datos estructurados
          await this.exportDataToExcel(data, fileName, "Datos");
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error exportando desde Shadow DOM:", error);
        throw error;
      }
    },
    extractDataFromShadowDOM(element, fieldMapping) {
      const data = [];

      // Buscar todas las tablas dentro del elemento
      const tables = element.querySelectorAll("table");

      if (tables.length > 0) {
        // Si hay tablas, extraer datos de la primera tabla
        const table = tables[0];
        const rows = table.querySelectorAll("tr");

        rows.forEach((row) => {
          const rowData = [];
          const cells = row.querySelectorAll("th, td");
          cells.forEach((cell) => {
            rowData.push(cell.textContent.trim());
          });
          if (rowData.length > 0) {
            data.push(rowData);
          }
        });
      } else {
        // Si no hay tablas, buscar campos con clase field-*
        const fieldSpans = element.querySelectorAll('[class*="field-"]');

        if (fieldSpans.length > 0) {
          const headers = [];
          const values = [];

          fieldSpans.forEach((span) => {
            const className = span.className;
            const fieldKey = className.match(/field-([^\s]+)/)?.[1];

            if (fieldKey) {
              const label = fieldMapping[fieldKey] || fieldKey;

              if (!headers.includes(label)) {
                headers.push(label);
                values.push(span.textContent.trim() || "");
              }
            }
          });

          if (headers.length > 0) {
            data.push(headers);
            data.push(values);
          }
        } else {
          // Extraer todo el texto como fallback
          const textContent = element.textContent.trim();
          if (textContent) {
            const lines = textContent.split("\n").filter((line) => line.trim());
            if (lines.length > 0) {
              data.push(["Contenido"]);
              lines.forEach((line) => {
                if (line.trim()) {
                  data.push([line.trim()]);
                }
              });
            }
          }
        }
      }

      return data;
    },
  },
};
</script>

<style scoped>
.shadow-container {
  width: 100%;
  min-height: 400px;
  border: 1px solid #ddd;
  border-radius: 4px;
  padding: 20px;
  background-color: #fff;
  margin-top: 1rem;
}

.shadow-host {
  width: 100%;
  min-height: 200px;
}

/* Mientras se resuelven shortcodes y vistas anidadas: spinner en lugar del hueco en blanco.
   Si ya había contenido pintado (re-render por cambio de datos), se atenúa. */
.vhc-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.75rem;
  min-height: 200px;
  color: #6c757d;
}

.vhc-loading-text {
  font-size: 0.95rem;
}

.shadow-host.is-rendering {
  opacity: 0.35;
  /* Sin min-height para que en la primera carga no quede un hueco bajo el spinner */
  min-height: 0;
}

/* Estilos para los botones de exportación */
.btn-group {
  margin-bottom: 1rem;
}

.btn-group .btn {
  margin-right: 0.5rem;
}
</style>
